import React from 'react';
import { Check, ChevronRight } from 'lucide-react';

/**
 * Phase 25.1.1 — Accounting Workflow Stepper.
 *
 * A compact horizontal step indicator that lives at the top of the
 * Carrier Import Detail page. Steps beyond the currently implemented
 * scope (`reconciliation`+) render as "coming soon" pips.
 */
const STEPS = [
  { key: 'upload',         label: 'Upload' },
  { key: 'mapping',        label: 'Mapping' },
  { key: 'reconciliation', label: 'Reconciliation' },
  { key: 'statements',     label: 'Statements' },
  { key: 'batch',          label: 'Batch' },
  { key: 'paid',           label: 'Paid' },
];

// Map back-end computed stages onto the 6-step visualization.
const STAGE_TO_STEP = {
  upload: 'upload',
  mapping: 'mapping',
  reconciliation: 'reconciliation',
  statements: 'statements',
  batch: 'batch',
  paid: 'paid',
  archived: 'upload',
};

function stateFor(stepKey, activeIndex, currentIndex) {
  if (stepKey === STEPS[currentIndex]?.key) return 'active';
  if (activeIndex > currentIndex) return 'done';
  return 'pending';
}

export function WorkflowStepper({ stage, className = '' }) {
  const activeKey = STAGE_TO_STEP[stage] || 'mapping';
  const activeIndex = STEPS.findIndex((s) => s.key === activeKey);

  return (
    <div
      className={`flex items-center gap-1 overflow-x-auto rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 ${className}`}
      data-testid="workflow-stepper"
    >
      {STEPS.map((step, i) => {
        const state = stateFor(step.key, activeIndex, i);
        const isDone = state === 'done';
        const isActive = state === 'active';
        return (
          <React.Fragment key={step.key}>
            <div
              className={`flex items-center gap-1.5 whitespace-nowrap ${
                isActive
                  ? 'text-slate-900 font-medium'
                  : isDone
                  ? 'text-emerald-700'
                  : 'text-slate-400'
              }`}
              data-testid={`stepper-step-${step.key}`}
              data-state={state}
            >
              <span
                className={`grid h-5 w-5 place-items-center rounded-full text-[10px] ${
                  isDone
                    ? 'bg-emerald-500 text-white'
                    : isActive
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {isDone ? <Check size={11} strokeWidth={3} /> : i + 1}
              </span>
              <span className="text-xs">{step.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight size={12} className="text-slate-300 shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default WorkflowStepper;
